import { Alert, Button, Dialog, Icon, Text, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { invoicePdfUrl, useCreateInvoice, useInvoice, useInvoiceAction } from '../api';
import { InvoiceRecipientDialog, type InvoiceRecipient } from './InvoiceRecipientDialog';

/** What the quick action needs to know about a sale. */
export interface QuickInvoiceSale {
  id: string;
  total_cents: number;
  customer?: { id: string; name: string } | null;
  invoice?: { id: string } | null;
}

export interface QuickInvoiceProps {
  sale: QuickInvoiceSale;
  onClose: () => void;
  /** Called with the new invoice's id, so the caller's sale knows it has one. */
  onCreated?: (invoiceId: string) => void;
}

/**
 * "Rechnung" as one step at the market stand (MKT-017): asks for the
 * recipient (anonymous sales), creates and finalises the invoice and then
 * offers PDF, sharing and e-mail. An existing invoice goes straight to the
 * last step.
 */
export function QuickInvoice({ sale, onClose, onCreated }: QuickInvoiceProps) {
  const { t } = useTranslation();
  const create = useCreateInvoice();
  const action = useInvoiceAction();
  const [invoiceId, setInvoiceId] = useState(sale.invoice?.id ?? null);
  const [failure, setFailure] = useState<unknown>(null);

  const run = async (recipient: InvoiceRecipient) => {
    setFailure(null);
    let id: string;
    try {
      id = (await create.mutateAsync({ sale_id: sale.id, ...recipient })).id;
    } catch (e) {
      setFailure(e);
      return;
    }
    setInvoiceId(id);
    onCreated?.(id);
    try {
      await action.mutateAsync({ id, action: 'finalize' });
    } catch (e) {
      // The draft stays; the result step says what is missing.
      setFailure(e);
    }
  };
  const close = () => {
    create.reset();
    action.reset();
    onClose();
  };

  if (invoiceId) {
    return <InvoiceResult id={invoiceId} failure={failure} onClose={close} />;
  }
  if (sale.customer) {
    return (
      <ConfirmDialog
        open
        onClose={close}
        tone="default"
        title={t('sales.recipient.title')}
        description={t('sales.quick.forCustomer', { name: sale.customer.name })}
        confirmLabel={t('sales.recipient.submit')}
        pending={create.isPending || action.isPending}
        error={failure ? errorMessage(failure) : undefined}
        onConfirm={() => void run({})}
      />
    );
  }
  return (
    <InvoiceRecipientDialog
      onClose={close}
      totalCents={sale.total_cents}
      pending={create.isPending || action.isPending}
      error={failure}
      onSubmit={(r) => void run(r)}
    />
  );
}

/** The finished invoice: view, share or send it. */
function InvoiceResult({
  id,
  failure,
  onClose,
}: {
  id: string;
  failure: unknown;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const invoice = useInvoice(id);
  const action = useInvoiceAction();
  const data = invoice.data;
  const final = data?.status === 'final';
  const email = data?.recipient?.email ?? data?.customer_email ?? null;
  const pdf = data ? invoicePdfUrl(data.id, data.version) : '';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // The share sheet takes the PDF as a file (phone), the link as a fallback.
  const share = async () => {
    if (!data) return;
    try {
      const blob = await (await fetch(pdf)).blob();
      const file = new File([blob], `${data.number ?? 'Rechnung'}.pdf`, {
        type: 'application/pdf',
      });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: data.number ?? '' });
      } else {
        await navigator.share({ url: new URL(pdf, window.location.href).toString() });
      }
    } catch (e) {
      // Cancelling the share sheet is no error.
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        toast.show(errorMessage(e), 'danger');
      }
    }
  };
  const send = () =>
    action.mutate(
      { id, action: 'send' },
      { onSuccess: () => toast.show(t('sales.invoice.sent', { to: email })) },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        final ? t('sales.quick.doneTitle', { number: data.number }) : t('sales.quick.draftTitle')
      }
      closeLabel={t('common.close')}
      size="sm"
      actions={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {(failure || action.error) && (
        <Alert tone="danger">{errorMessage(failure ?? action.error)}</Alert>
      )}
      {data && !final && (
        <Text>
          <Link to={`/verkauf/rechnungen/${data.id}`}>{t('sales.quick.openDraft')}</Link>
        </Text>
      )}
      {final && (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            onClick={() => window.open(pdf, '_blank', 'noopener')}
            iconStart={<Icon name="external-link" size="sm" />}
          >
            {t('sales.quick.showPdf')}
          </Button>
          {canShare && (
            <Button variant="secondary" onClick={() => void share()}>
              {t('sales.quick.share')}
            </Button>
          )}
          {email ? (
            <Button variant="secondary" loading={action.isPending} onClick={send}>
              {t('sales.quick.sendTo', { to: email })}
            </Button>
          ) : (
            <Text size="sm" tone="muted">
              {t('sales.quick.noEmail')}
            </Text>
          )}
        </div>
      )}
    </Dialog>
  );
}
